import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { RenderableMenu } from '@rocksoft/cdk';
import { combineLatest, concat, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'era-menus',
  templateUrl: './menus.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, AsyncPipe],
})
export class MenusComponent {
  readonly menus = input.required<RenderableMenu[]>();

  /**
   * Stable Playwright selector. Combines URL path and menu title so entries stay unique
   * even when routes reuse the same title.
   */
  menuLinkTestId(menu: RenderableMenu): string {
    return `menu-link-${this.pathToSlug(menu.url)}-${this.titleToSlug(menu.title)}`;
  }

  private pathToSlug(url: string): string {
    const pathOnly = (url || '/').split(/[?#]/)[0] ?? '';
    const collapsed = pathOnly.replace(/\/{2,}/g, '/');
    const noTrailing = collapsed.replace(/\/+$/, '');
    const trimmed = noTrailing.startsWith('/') ? noTrailing.slice(1) : noTrailing;
    return trimmed.length === 0 ? 'root' : trimmed.replace(/\//g, '-');
  }

  private titleToSlug(title: string): string {
    return (title || 'item')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  public isActiveOrLastMatched$(menu: RenderableMenu): Observable<boolean> {
    const hasChildren$ = concat(of(null), menu.children$.pipe(map((children) => children && children.length > 0)));

    return combineLatest([hasChildren$, menu.isMatched$, menu.isActive$]).pipe(
      map(([hasChildren, isMatched, isActive]) => isMatched || (!hasChildren && isActive))
    );
  }
}
